import { Dialog, DialogContent } from '@mui/material'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import NewModalButtons from 'ui/molecules/ModalButtons/NewModalButtons'
import { DialogContentData, Subtype } from '../PublishOptimizationResultButton.d'
import DialogDescription from './DialogDescription'
import DialogPlanVisualization from './DialogPlanVisualization'
import InfoBannerWithTooltip from './InfoBannerWithTooltip'

type ConfirmationDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  isPublishing: boolean
  dialogContent: DialogContentData
  selectedSubtype: Subtype | null
}

export default function ConfirmationDialog({
  isOpen,
  onClose,
  onSubmit,
  isPublishing,
  dialogContent,
  selectedSubtype,
}: ConfirmationDialogProps): ReactElement {
  const { t } = useTranslation()

  const getDialogTitle = () => {
    if (dialogContent.isMultipleSubtypes) {
      return t('Are you sure you want to publish all production plans?')
    }

    if (selectedSubtype) {
      return t('Are you sure you want to publish the production plan for {{subtype}}?', {
        subtype: selectedSubtype.subtype_display_name,
      })
    }

    return t('Are you sure you want to publish the production plan?')
  }

  const disableSubmit =
    isPublishing ||
    dialogContent.allAlreadyPublished ||
    (dialogContent.state !== 'ok_to_publish' && dialogContent.state !== 'initial')

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle title={getDialogTitle()} handleClose={onClose} />
      <DialogContent>
        <InfoBannerWithTooltip />
        <DialogDescription dialogContent={dialogContent} selectedSubtype={selectedSubtype} />
        <DialogPlanVisualization dialogContent={dialogContent} />
        <NewModalButtons
          submitLabel={t('Publish production plan')}
          isSubmitting={isPublishing}
          disableSubmit={disableSubmit}
          submitVariant="success"
          icon="far fa-rocket"
          onCancel={onClose}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  )
}
